import { RSAA } from 'redux-api-middleware'
import { getApiPath } from 'sidecar-utility-library'

import { types, initialState } from './types'

export const savePaymentQuestions = (expenseUuid, paymentData) => {
  const isManualExpense = paymentData.expenseType !== 'SWIPE'

  // Build form fields array based on what questions are actually shown
  const formFields = []

  // Only include MEMBER_PAID_AMOUNT for manual expenses (when "How much did you pay?" is shown)
  if (
    isManualExpense &&
    paymentData.amountPaid !== undefined &&
    paymentData.amountPaid !== ''
  ) {
    formFields.push({
      field: 'MEMBER_PAID_AMOUNT',
      value: paymentData.amountPaid,
    })
  }

  // Include BALANCE_STILL_DUE when the value is not empty
  // (when "Is there a balance still due?" is shown and answered)
  // Note: Previously excluded when proofOfPaymentEnabled was true, but the question
  // can still be shown to users even when that flag is on, so we should save their answer
  if (
    paymentData.isBalanceDue !== undefined &&
    paymentData.isBalanceDue !== null
  ) {
    formFields.push({
      field: 'BALANCE_STILL_DUE',
      value: paymentData.isBalanceDue ? 'true' : 'false',
    })
  }

  // Add bundle info if a bundle was selected or user chose "No"
  if (paymentData.bundleInfo !== null && paymentData.bundleInfo !== undefined) {
    formFields.push({
      field: 'BUNDLE_INFO',
      value: JSON.stringify(paymentData.bundleInfo), // Always JSON stringify (empty object {} for "No", full object for bundle)
    })
  }

  return {
    [RSAA]: {
      endpoint: getApiPath('exp', `expenses/${expenseUuid}/expense-form`),
      method: 'PUT',
      body: JSON.stringify({
        formType: 'PAYMENT_INFO',
        formFields,
      }),
      types: [
        types.SAVE_PAYMENT_QUESTIONS_REQUEST,
        {
          type: types.SAVE_PAYMENT_QUESTIONS_SUCCESS,
          meta: { expenseUuid },
        },
        types.SAVE_PAYMENT_QUESTIONS_ERROR,
      ],
    },
  }
}

export const fetchPaymentQuestions = expenseUuid => ({
  [RSAA]: {
    endpoint: getApiPath(
      'exp',
      `expenses/${expenseUuid}/expense-form?formType=PAYMENT_INFO`,
    ),
    method: 'GET',
    types: [
      types.FETCH_PAYMENT_QUESTIONS_REQUEST,
      {
        type: types.FETCH_PAYMENT_QUESTIONS_SUCCESS,
        meta: { expenseUuid },
      },
      types.FETCH_PAYMENT_QUESTIONS_ERROR,
    ],
  },
})

export const clearPaymentQuestionsError = () => ({
  type: types.CLEAR_PAYMENT_QUESTIONS_ERROR,
})

/**
 * Optimistic patch fired after `linkExpenses` succeeds — inserts (or replaces)
 * a synthetic `LINKED_EXPENSE` field on `paymentQuestions.entity.formFields` so
 * `selectLinkedExpenseFor` returns the freshly-linked expense without waiting
 * on the PAYMENT_INFO refetch. The refetch that follows just reconciles with
 * authoritative BE data.
 *
 * @param {string} expenseUuid Source expense UUID (the one being viewed).
 * @param {object} linkedExpense Candidate details to serialize into the field.
 * @param {string} linkedExpense.uuid Target expense UUID.
 * @param {string} [linkedExpense.title]
 * @param {number} [linkedExpense.amountCents] Target amount in cents.
 */
export const patchPaymentQuestionsLinkedExpense = (
  expenseUuid,
  linkedExpense,
) => ({
  type: types.PATCH_PAYMENT_QUESTIONS_LINKED_EXPENSE,
  meta: { expenseUuid },
  payload: linkedExpense,
})

export const clearPaymentQuestionsData = () => ({
  type: types.CLEAR_PAYMENT_QUESTIONS_DATA,
})

export default (
  state = initialState,
  { type, payload = {}, meta = {} } = {},
) => {
  switch (type) {
    case types.SAVE_PAYMENT_QUESTIONS_REQUEST:
    case types.FETCH_PAYMENT_QUESTIONS_REQUEST: {
      return {
        ...state,
        paymentQuestions: {
          ...state.paymentQuestions,
          busy: true,
          error: null,
        },
      }
    }

    case types.SAVE_PAYMENT_QUESTIONS_SUCCESS:
    case types.FETCH_PAYMENT_QUESTIONS_SUCCESS: {
      return {
        ...state,
        paymentQuestions: {
          ...state.paymentQuestions,
          busy: false,
          entity: payload,
          error: null,
        },
      }
    }

    case types.SAVE_PAYMENT_QUESTIONS_ERROR:
    case types.FETCH_PAYMENT_QUESTIONS_ERROR: {
      return {
        ...state,
        paymentQuestions: {
          ...state.paymentQuestions,
          busy: false,
          error: payload.message || 'An error occurred',
        },
      }
    }

    case types.CLEAR_PAYMENT_QUESTIONS_ERROR: {
      return {
        ...state,
        paymentQuestions: {
          ...state.paymentQuestions,
          error: null,
        },
      }
    }

    case types.CLEAR_PAYMENT_QUESTIONS_DATA: {
      return {
        ...state,
        paymentQuestions: {
          busy: false,
          entity: {},
          error: null,
        },
      }
    }

    case types.PATCH_PAYMENT_QUESTIONS_LINKED_EXPENSE: {
      const entity = state.paymentQuestions?.entity ?? {}
      // Stale-source guard: if the currently-loaded PAYMENT_INFO entity is
      // for a different expense than the one the patch targets, drop the
      // patch so a delayed link on expense A can't stamp a bogus row onto
      // expense B's entity after the user navigated.
      const entityUuid = entity.expenseUuid ?? entity.uuid
      const targetUuid = meta?.expenseUuid
      if (entityUuid && targetUuid && entityUuid !== targetUuid) {
        return state
      }
      const currentFields = Array.isArray(entity.formFields)
        ? entity.formFields
        : []
      const linked = payload || {}
      // Match the BE payload shape parsed in `selectors.parseLinkedExpenseFieldValue`.
      const serialized = JSON.stringify({
        expenseUuid: linked.uuid ?? '',
        expenseId: linked.expenseId ?? null,
        title: linked.title ?? '',
        createdDate: linked.createdAt ?? '',
        status: linked.status ?? '',
        swipeAmount:
          typeof linked.amountCents === 'number'
            ? linked.amountCents / 100
            : null,
      })
      const linkedField = { field: 'LINKED_EXPENSE', value: serialized }
      const withoutExisting = currentFields.filter(
        f => f?.field !== 'LINKED_EXPENSE',
      )
      return {
        ...state,
        paymentQuestions: {
          ...state.paymentQuestions,
          entity: {
            ...entity,
            formFields: [...withoutExisting, linkedField],
          },
        },
      }
    }

    default:
      return state
  }
}
